import { useMemo } from 'react'
import { toast } from 'react-toastify'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import useReduxAction from '@hooks/useReduxAction'

import BulkCreateAgencyProviderPackagesModal from '@modals/BulkCreateAgencyProviderPackagesModal'
import BulkCreateBrandProviderModal from '@modals/BulkCreateBrandProviderModal'
import CreateOrEditAgencyProviderModal from '@modals/CreateOrEditAgencyProviderModal'
import CreateProviderPackageItemModal from '@modals/CreateProviderPackageItemModal'
import DuplicateProviderPackageModal from '@modals/DuplicateProviderPackageModal'
import ManageActivityModal from '@modals/ManageActivityModal'
import UploadEntityAssetsModal from '@modals/UploadEntityAssetsModal'

import PageContent from './components/PageContent'

const createComment = (params, createFn) => {
  createFn(params, { ...defaultRequestOptions.comment, ...defaultRequestOptions.conversation })
    .then(({ success, errors }) => {
      if (!success && errors){
        toast.warn(errors[0])
      }
    })
}

const callbacks = (component, setState) => {
  const componentCallbacks = {
    BulkCreateAgencyProviderPackagesModal: {
      closeModal: () => setState({ showBulkCreateAgencyProviderPackagesModal: false }),
      bulkCreateAgencyProviderPackages: payload => handleCallbackAction(payload),
    },
    BulkCreateBrandProviderModal: {
      closeModal: () => setState({ showBulkCreateBrandProviderModal: false }),
      bulkCreateBrandProviders: payload => handleCallbackAction(payload),
    },
    CreateOrEditAgencyProviderModal: {
      closeModal: () => setState({ showCreateOrEditAgencyProviderModal: false }),
      createAgencyProvider: payload => handleCallbackAction(payload),
      updateAgencyProvider: payload => handleCallbackAction(payload),
    },
    CreateProviderPackageItemModal: {
      closeModal: () => setState({ showCreateProviderPackageItemModal: false }),
      createProviderPackageItem: payload => handleCallbackAction(payload),
    },
    DuplicateProviderPackageModal: {
      closeModal: () => setState({ showDuplicateProviderPackageModal: false }),
      duplicateProviderPackage: payload => handleCallbackAction(payload),
    },
    ManageActivityModal: {
      closeModal: () => setState({ showManageActivityModal: false }),
    },
    UploadEntityAssetsModal: {
      closeModal: () => setState({ showUploadEntityAssetsModal: false }),
      deleteAttachment: payload => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const defaultState = {
  showBulkCreateAgencyProviderPackagesModal: false,
  showBulkCreateBrandProviderModal: false,
  showCreateOrEditAgencyProviderModal: false,
  showCreateProviderPackageItemModal: false,
  showDuplicateProviderPackageModal: false,
  showManageActivityModal: false,
  showUploadEntityAssetsModal: false,
}

const Edit = () => {
  const { providerPackageId } = useNumericParams()

  const [state, setState] = useSetState(defaultState)
  const {
    showBulkCreateAgencyProviderPackagesModal,
    showBulkCreateBrandProviderModal,
    showCreateOrEditAgencyProviderModal,
    showCreateProviderPackageItemModal,
    showDuplicateProviderPackageModal,
    showManageActivityModal,
    showUploadEntityAssetsModal,
  } = state

  useReduxAction(
    'providerPackages',
    'loadProviderPackage',
    {
      entityId: providerPackageId,
      ...defaultRequestOptions.providerPackage,
      include_provider_logo: true,
    },
    [providerPackageId],
    {
      dispatchAction: (action, requestOptions) => action(providerPackageId, requestOptions),
      shouldPerformFn: ({ loading }) => providerPackageId && !loading,
    },
  )

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
    topModalKey,
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      createComment,
      deleteAgencyProviderPackage: payload => handleCallbackAction(payload),
      deleteAttachment: payload => handleCallbackAction(payload),
      deleteProviderPackage: payload => handleCallbackAction(payload),
      deleteProviderPackageItem: payload => handleCallbackAction(payload),
      showBulkCreateAgencyProviderPackagesModal: (payload) => {
        setModalData('BulkCreateAgencyProviderPackagesModal', payload)
        setState({ showBulkCreateAgencyProviderPackagesModal: true })
      },
      showBulkCreateBrandProviderModal: (payload) => {
        setModalData('BulkCreateBrandProviderModal', payload)
        setState({ showBulkCreateBrandProviderModal: true })
      },
      showCreateOrEditAgencyProviderModal: (payload) => {
        setModalData('CreateOrEditAgencyProviderModal', payload)
        setState({ showCreateOrEditAgencyProviderModal: true })
      },
      showCreateProviderPackageItemModal: (payload) => {
        setModalData('CreateProviderPackageItemModal', payload)
        setState({ showCreateProviderPackageItemModal: true })
      },
      showDuplicateProviderPackageModal: (payload) => {
        setModalData('DuplicateProviderPackageModal', payload)
        setState({ showDuplicateProviderPackageModal: true })
      },
      showManageActivityModal: (payload) => {
        setModalData('ManageActivityModal', payload)
        setState({ showManageActivityModal: true })
      },
      showUploadEntityAssetsModal: (payload) => {
        setModalData('UploadEntityAssetsModal', payload)
        setState({ showUploadEntityAssetsModal: true })
      },
      updateProviderPackage: payload => handleCallbackAction(payload),
    },
  }), [topModalKey])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent providerPackageId={providerPackageId} />

        <BulkCreateAgencyProviderPackagesModal
          callbacks={callbacks('BulkCreateAgencyProviderPackagesModal', setState)}
          showModal={showBulkCreateAgencyProviderPackagesModal}
        />

        <BulkCreateBrandProviderModal
          callbacks={callbacks('BulkCreateBrandProviderModal', setState)}
          showModal={showBulkCreateBrandProviderModal}
        />

        <CreateOrEditAgencyProviderModal
          callbacks={callbacks('CreateOrEditAgencyProviderModal', setState)}
          showModal={showCreateOrEditAgencyProviderModal}
        />

        <CreateProviderPackageItemModal
          callbacks={callbacks('CreateProviderPackageItemModal', setState)}
          showModal={showCreateProviderPackageItemModal}
        />

        <DuplicateProviderPackageModal
          callbacks={callbacks('DuplicateProviderPackageModal', setState)}
          showModal={showDuplicateProviderPackageModal}
        />

        <ManageActivityModal
          callbacks={callbacks('ManageActivityModal', setState)}
          showModal={showManageActivityModal}
        />

        <UploadEntityAssetsModal
          callbacks={callbacks('UploadEntityAssetsModal', setState)}
          showModal={showUploadEntityAssetsModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Edit
